<template>
  <section class="rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-sm font-semibold text-slate-900">评价结果</h3>
        <p class="mt-0.5 text-xs text-slate-500">总结提交后的正式评价</p>
      </div>
      <span v-if="currentEvaluation" class="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
        {{ gradeLabel(currentEvaluation.grade) }}
      </span>
    </div>

    <div v-if="currentEvaluation" class="mt-3 space-y-3">
      <div class="flex flex-wrap items-start justify-between gap-3 rounded-md bg-white px-3 py-3 ring-1 ring-slate-200">
        <div class="flex min-w-0 items-center gap-2">
          <div class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-medium text-slate-600">
            <img
              v-if="evaluatorAvatarUrl(currentEvaluation)"
              :src="evaluatorAvatarUrl(currentEvaluation)"
              :alt="`${evaluatorName(currentEvaluation)}头像`"
              class="h-full w-full object-cover"
            >
            <span v-else>{{ initials(currentEvaluation) }}</span>
          </div>
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-slate-800">{{ evaluatorName(currentEvaluation) }}</p>
            <p class="text-xs text-slate-400">{{ currentEvaluation.evaluatedAt || '--' }}</p>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-2" aria-label="评价星级">
          <div class="flex items-center gap-0.5">
            <StarIcon
              v-for="score in 5"
              :key="score"
              class="h-4 w-4"
              :class="score <= gradeScore(currentEvaluation.grade) ? 'text-amber-400' : 'text-slate-200'"
              aria-hidden="true"
            />
          </div>
          <span class="text-xs font-medium text-slate-500">{{ gradeScore(currentEvaluation.grade) || '-' }}/5</span>
        </div>
      </div>
      <p class="whitespace-pre-wrap break-words text-sm leading-6 text-slate-600">{{ currentEvaluation.comment || '暂无评价意见' }}</p>
    </div>

    <div v-else class="mt-3 rounded-md border border-dashed border-slate-200 bg-white px-3 py-4 text-sm text-slate-400">
      暂无评价
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { StarIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  evaluations: {
    type: Array,
    default: () => []
  }
})

const gradeLabels = {
  EXCELLENT: '优秀',
  GOOD: '良好',
  MEETS_EXPECTATIONS: '符合要求',
  NEEDS_IMPROVEMENT: '有待改进',
  UNQUALIFIED: '不合格'
}

const gradeScores = {
  EXCELLENT: 5,
  GOOD: 4,
  MEETS_EXPECTATIONS: 3,
  NEEDS_IMPROVEMENT: 2,
  UNQUALIFIED: 1
}

const currentEvaluation = computed(() => {
  const evaluations = Array.isArray(props.evaluations) ? props.evaluations : []
  return evaluations.find((evaluation) => evaluation.current && evaluation.phase === 'SUMMARY')
    || null
})

function gradeLabel(grade) {
  return gradeLabels[grade] || '已评价'
}

function gradeScore(grade) {
  return gradeScores[grade] || 0
}

function evaluatorName(evaluation) {
  return evaluation?.evaluatorName
    || evaluation?.evaluatorDisplayName
    || evaluation?.evaluatorUserName
    || evaluation?.evaluator?.displayName
    || evaluation?.evaluator?.name
    || evaluation?.evaluator?.realName
    || evaluation?.evaluator?.username
    || (evaluation?.evaluatorUserId ? `用户 ${evaluation.evaluatorUserId}` : '未署名')
}

function evaluatorAvatarUrl(evaluation) {
  return evaluation?.evaluatorAvatarUrl
    || evaluation?.evaluatorAvatarURL
    || evaluation?.avatarUrl
    || evaluation?.evaluator?.avatarUrl
    || evaluation?.evaluator?.avatarURL
    || evaluation?.evaluatorAvatar
    || evaluation?.avatar
    || evaluation?.evaluator?.avatar
    || ''
}

function initials(evaluation) {
  const name = evaluatorName(evaluation)
  if (name === '未署名') return '?'
  return name.slice(0, 2).toUpperCase()
}
</script>
